import { useCallback, useRef } from 'react'
import Axios, { type AxiosResponse, type Method } from 'axios'
import { useAsyncCall, type AsyncCallResponse } from '~/api/useAsyncCall'

import { arrayBufferToBlob, arrayBufferToString } from '~/common/util/binaryUtil'

export const getData = <T>(url: string, method: Method = 'GET') =>
  Axios.request<T>({
    url: `/api/${url}`,
    responseType: 'json',
    method: method,
  }).then(({ data }) => data)

export const deleteData = <T>(url: string) =>
  Axios.request<T>({
    url: `/api/${url}`,
    method: 'DELETE',
  }).then(({ data }) => data)

export const putData = <T>(url: string, data?: T) =>
  Axios.request<T>({
    url: `/api/${url}`,
    responseType: 'json',
    method: 'PUT',
    ...(data ? { data } : {}),
  }).then(({ data }) => data)

export const postData = <T>(url: string, method: Method, data: T) =>
  Axios.request<T>({
    url: `/api/${url}`,
    responseType: 'json',
    method: method,
    data: data,
  }).then(({ status }) => status)

export const useDataAsyncCall = <T>(
  url: string,
  method: Method = 'GET',
  params: any = undefined,
  disabled = false // The disabled argument can be used to make the async call conditional
): AsyncCallResponse<T | null> => {
  const abortControllerRef = useRef<AbortController>(new AbortController())

  const asyncCall = useCallback(() => {
    abortControllerRef.current.abort()
    abortControllerRef.current = new AbortController()
    return Axios.request<T>({
      url: `/api/${url}`,
      responseType: 'json',
      method,
      params,
      signal: abortControllerRef.current.signal,
    })
  }, [url, method, params])

  const emptyResolver = useCallback(() => {
    return Promise.resolve({
      data: undefined,
      status: 200,
      statusText: 'OK',
      headers: {},
      config: {},
      request: {},
    } as AxiosResponse<T>)
  }, [])

  const [response, meta] = useAsyncCall<AxiosResponse<T>>(disabled ? emptyResolver : asyncCall)
  return [response?.data || null, { ...meta }]
}

const initiateBlobDownload = (blob: Blob, filename = 'download') => {
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.setAttribute('target', '__blank')
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

export const downloadReport = (promise: Promise<Blob>, filename: string) => {
  return promise
    .then((data) => {
      initiateBlobDownload(data, filename)
    })
    .catch((e) => {
      return e.data
    })
}

export const handleArrayBufferDownload =
  (filename: string) =>
  ({ data }: AxiosResponse<ArrayBuffer>) => {
    initiateBlobDownload(arrayBufferToBlob(data), filename)
  }

export const getDownload = (url: string, filename: string) =>
  Axios.request<ArrayBuffer>({
    url,
    method: 'GET',
    responseType: 'arraybuffer',
  })
    .then(handleArrayBufferDownload(filename))
    .catch((e) => {
      return arrayBufferToString(e.response.data)
    })

export const apiLogin = () => Axios.get('/api/login/current')
